import { TemplateSection } from "./report-template";
import { ReportPreviewMetadata, PropertyPreviewMetadata, FieldType, UpdateMetadata } from "../../../_services/offline-report-preview.service";
import { Subscription } from "rxjs";
import { ReportElement, ReportUnits, ElementType } from "../../../_objects/report-element";

export class ReportSection extends ReportElement {
  public top: string;
  public left: string;
  public width: string;
  public height: string;
  public zIndex: number;
  public stageId: string;

  private units: ReportUnits;

  private baseDef: TemplateSection;
  private readonly metaTop = "y";
  private readonly metaLeft = "x";
  private readonly metaWidth = "width";
  private readonly metaHeight = "height";
  private readonly metaStageId = "stageId";

  constructor(index: number, units: ReportUnits, templateSection?: TemplateSection) {
    super(ElementType.Section, index, !(templateSection == null), templateSection ? templateSection.id : null);

    this.units = units;

    if (templateSection) {
      this.baseDef = templateSection;
      this.baseDef.id = this.baseDef.id ? this.baseDef.id : this.generateUUID();
    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        zIndex: 0,
        stageId: ""
      };
    }

    this.top = this.units.concatenate("sizeUnit", this.baseDef.y);
    this.left = this.units.concatenate("sizeUnit", this.baseDef.x);
    this.width = this.units.concatenate("sizeUnit", this.baseDef.width);
    this.height = this.units.concatenate("sizeUnit", this.baseDef.height);
    this.zIndex = this.baseDef.zIndex;
    this.stageId = this.baseDef.stageId;

    this.setChainUpdate();
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx("sizeUnit", metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx("sizeUnit", metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx("sizeUnit", metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx("sizeUnit", metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx("sizeUnit", metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx("sizeUnit", metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx("sizeUnit", metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx("sizeUnit", metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx("sizeUnit", metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx("sizeUnit", metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight]
    });
  }

  public getLeftPx(): number {
    return this.units.toPx("sizeUnit", this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx("sizeUnit", this.baseDef[this.metaTop]);
  }
  public getWidthPx(): number {
    return this.units.toPx("sizeUnit", this.baseDef[this.metaWidth]);
  }
  public getHeightPx(): number {
    return this.units.toPx("sizeUnit", this.baseDef[this.metaHeight]);
  }

  public getTemplateJSON(): TemplateSection {
    return this.baseDef;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaStageId,
        parentName: null,
        value: "",
        isFieldType: (type: FieldType): boolean => type === "Select",
        selectItems: [],
        isRequired: false,
        isEdited: false
      },
      {
        name: this.metaLeft,
        parentName: null,
        value: "",
        isFieldType: (type: FieldType): boolean => type === "Number",
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: "Position"
      },
      {
        name: this.metaTop,
        parentName: null,
        value: "",
        isFieldType: (type: FieldType): boolean => type === "Number",
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: "Position"
      },
      {
        name: this.metaHeight,
        parentName: null,
        value: "",
        isFieldType: (type: FieldType): boolean => type === "Number",
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: "Size"
      },
      {
        name: this.metaWidth,
        parentName: null,
        value: "",
        isFieldType: (type: FieldType): boolean => type === "Number",
        selectItems: [],
        isRequired: true,
        isEdited: false,
        group: "Size"
      }
    ];
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate("sizeUnit", metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate("sizeUnit", metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.units.concatenate("sizeUnit", metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.units.concatenate("sizeUnit", metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaStageId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.stageId = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          value: this.convertUnit(this.baseDef[this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          value: this.convertUnit(this.baseDef[this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaHeight]
        });
      })
    );
  }
}
